import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { API, axios } from "@/api";
import {
  Loader2, Layers, Filter, ChevronDown, ChevronRight, X, Play, Pause,
  Download, Maximize2, ArrowLeft, BarChart3, Activity, Zap, AlertTriangle,
  Check, Eye, Plus, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  Legend as RLegend, ResponsiveContainer,
} from "recharts";

// ─── Constants ────────────────────────────────────────────────────
const STATUS_COLORS = { gray: "#94a3b8", blue: "#3b82f6", yellow: "#eab308", orange: "#f97316", green: "#22c55e", red: "#ef4444", hollow: "transparent" };
const STATUS_LABELS = { gray: "Not started", blue: "Phase 1-3", yellow: "Phase 4-5", orange: "Phase 6-7", green: "Complete", red: "Failed/Stuck", hollow: "Gap (>50K pop)" };
const CONF_COLORS = { low: "#ef4444", mid: "#f97316", high: "#eab308", top: "#22c55e" };
const METRIC_COLORS = { businesses: "#3b82f6", offers: "#f59e0b", emails: "#8b5cf6", google_spend: "#ef4444", cities_completed: "#22c55e" };
const TIME_RANGES = [{ key: "7", label: "1W" }, { key: "30", label: "1M" }, { key: "90", label: "3M" }, { key: "365", label: "1Y" }];
const DEFAULT_LAYERS = { allCities: true, categoryGaps: true, foursquareWarning: true };
const FILTER_PRESETS = [
  { name: "California In Progress", filters: { states: ["CA"], phases: [1,2,3,4,5] } },
  { name: "High Population Not Started", filters: { minPop: 200000, phases: [0] } },
  { name: "Email Ready for Outreach", filters: { minEmailPct: 30 } },
  { name: "Needs Category Gap Fill", filters: { categoryGaps: true } },
];

const ACCORDION_CATS = [
  {
    id: "business", name: "Business Data", filters: [
      { id: "minOffers", label: "Birthday Offers Confirmed", type: "range", min: 0, max: 500 },
      { id: "minBiz", label: "Businesses Discovered", type: "range", min: 0, max: 5000 },
      { id: "categoryCoverage", label: "Category Coverage", type: "select", options: ["all","has_gaps","fully_covered","not_started"] },
      { id: "minEmailPct", label: "Email Coverage %", type: "range", min: 0, max: 100 },
      { id: "minRedemptionRate", label: "Redemption Success Rate", type: "range", min: 0, max: 100 },
    ],
  },
  {
    id: "geographic", name: "Geographic", filters: [
      { id: "counties", label: "County", type: "multi" },
      { id: "minPop", label: "Population Range", type: "range", min: 0, max: 5000000 },
      { id: "states", label: "State", type: "multi", options: ["CA","TX","NY","FL","IL","PA","OH","GA","NC","MI","NJ","VA","WA","AZ","MA","TN","IN","MO","MD","WI","CO","MN","SC","AL","LA","KY","OR","OK","CT","UT","IA","NV","AR","MS","KS","NM","NE","ID","WV","HI","NH","ME","MT","RI","DE","SD","ND","AK","VT","WY","DC"] },
    ],
  },
  {
    id: "google", name: "Google Places", filters: [
      { id: "maxGoogleSpend", label: "Budget Spent", type: "range", min: 0, max: 300 },
      { id: "minGoogleCalls", label: "Calls Used", type: "range", min: 0, max: 500 },
    ],
  },
  {
    id: "pipeline", name: "Pipeline Status", filters: [
      { id: "dateAdded", label: "Date Added", type: "daterange" },
      { id: "dateCompleted", label: "Date Completed", type: "daterange" },
      { id: "sweepNight", label: "Sweep Night", type: "range", min: 0, max: 10 },
      { id: "phases", label: "Sweep Phase", type: "checkboxes", options: [0,1,2,3,4,5,6,7] },
    ],
  },
  {
    id: "quality", name: "Quality Metrics", filters: [
      { id: "minConfidence", label: "Confidence Score", type: "range", min: 0, max: 1, step: 0.1 },
      { id: "sourceUsed", label: "Source Used", type: "multi", options: ["overture","osm","tomtom","geoapify","here","foursquare","outscraper","hunter","abstractapi","openweb_ninja"] },
      { id: "staleness", label: "Staleness Level", type: "checkboxes", options: ["fresh","recent","stale","unknown"] },
    ],
  },
  {
    id: "special", name: "Special Flags", filters: [
      { id: "categoryGaps", label: "Category Gaps", type: "toggle" },
      { id: "emailReady", label: "Email Ready (>30%)", type: "toggle" },
      { id: "foursquareWarn", label: "Foursquare Warning", type: "toggle" },
      { id: "foodTruck", label: "Food Truck Concentration", type: "toggle" },
      { id: "trueFreebie", label: "True Freebie Coverage", type: "toggle" },
    ],
  },
  {
    id: "system", name: "System Health", filters: [
      { id: "circuitBreaker", label: "Circuit Breaker Status", type: "multi", options: ["tripped","clear"] },
      { id: "dlqItems", label: "Dead Letter Queue Items", type: "toggle" },
      { id: "gapFiller", label: "Gap Filler Ran", type: "toggle" },
      { id: "googlePending", label: "Google Validation Pending", type: "toggle" },
      { id: "sourceHealth", label: "Source Health Status", type: "multi", options: ["healthy","warning","critical"] },
    ],
  },
];

const ALL_LAYERS = [
  { id: "allCities", label: "All Cities" }, { id: "completeOnly", label: "Complete Only" },
  { id: "inProgress", label: "In Progress" }, { id: "notStarted", label: "Not Started" },
  { id: "flagged", label: "Flagged/Stuck" }, { id: "coverageGaps", label: "Coverage Gaps" },
  { id: "popHeat", label: "Population Density Heat" }, { id: "offerHeat", label: "Birthday Offers Heat" },
  { id: "emailCoverage", label: "Email Coverage" }, { id: "googleBudget", label: "Google Budget" },
  { id: "foursquareWarning", label: "Foursquare Warning" }, { id: "foodTrucks", label: "Food Trucks" },
  { id: "categoryGaps", label: "Category Gaps" }, { id: "confidenceGradient", label: "Confidence Gradient" },
];

// ─── Pin size from population ─────────────────────────────────────
function pinRadius(pop) { return Math.max(8, Math.min(24, Math.round(8 + (pop / 500000) * 16))); }

// ─── Confidence to gradient color ─────────────────────────────────
function confColor(score) {
  if (score < 0.4) return CONF_COLORS.low;
  if (score < 0.6) return CONF_COLORS.mid;
  if (score < 0.8) return CONF_COLORS.high;
  return CONF_COLORS.top;
}

// ─── ZoomWatcher ──────────────────────────────────────────────────
function ZoomWatcher({ onZoom }) {
  useMapEvents({ zoomend: (e) => onZoom(e.target.getZoom()) });
  return null;
}

// ─── Sweep Pulse Animation CSS ────────────────────────────────────
const pulseStyle = `@keyframes sweepPulse { 0% { box-shadow: 0 0 0 0 rgba(59,130,246,0.6); } 70% { box-shadow: 0 0 0 12px rgba(59,130,246,0); } 100% { box-shadow: 0 0 0 0 rgba(59,130,246,0); } }`;

// ─── Main Component ───────────────────────────────────────────────
export default function CoverageMapPanel({ fullscreen = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(6);
  const [layers, setLayers] = useState({ ...DEFAULT_LAYERS });
  const [showLayers, setShowLayers] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showLegend, setShowLegend] = useState(true);
  const [viewMode, setViewMode] = useState("phase"); // phase | confidence
  const [filters, setFilters] = useState({});
  const [expandedAccordion, setExpandedAccordion] = useState(null);
  const [timeRange, setTimeRange] = useState("7");
  const [metricsData, setMetricsData] = useState([]);
  const [activeMetrics, setActiveMetrics] = useState({ businesses: true, offers: true });
  const [scrubberDay, setScrubberDay] = useState(30);
  const [playing, setPlaying] = useState(false);
  const playRef = useRef(null);
  const [showExport, setShowExport] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/pipeline/coverage-map`);
      setData(res.data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, []);

  const fetchMetrics = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/pipeline/metrics-timeline?days=${timeRange}`);
      setMetricsData(res.data.timeline || []);
    } catch { setMetricsData([]); }
  }, [timeRange]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  // Scrubber play/pause
  useEffect(() => {
    if (playing) {
      playRef.current = setInterval(() => {
        setScrubberDay(d => { if (d >= 30) { setPlaying(false); return 30; } return d + 1; });
      }, 500);
    }
    return () => clearInterval(playRef.current);
  }, [playing]);

  // Apply filters to cities
  const filteredCities = useMemo(() => {
    if (!data?.cities) return [];
    let cities = data.cities.filter(c => c.lat && c.lng);

    // Layer filters
    if (!layers.allCities) {
      if (layers.completeOnly) cities = cities.filter(c => c.status === "green");
      if (layers.inProgress) cities = cities.filter(c => ["blue","yellow","orange"].includes(c.status));
      if (layers.notStarted) cities = cities.filter(c => c.status === "gray" || c.status === "hollow");
      if (layers.flagged) cities = cities.filter(c => c.status === "red");
    }

    // Accordion filters
    if (filters.states?.length) cities = cities.filter(c => filters.states.includes(c.state_code));
    if (filters.phases?.length) cities = cities.filter(c => filters.phases.includes(c.current_phase));
    if (filters.minConfidence) cities = cities.filter(c => c.confidence_score >= filters.minConfidence);
    if (filters.minPop) cities = cities.filter(c => (c.population || 0) >= filters.minPop);
    if (filters.minEmailPct) cities = cities.filter(c => c.email_coverage_pct >= filters.minEmailPct);
    if (filters.categoryGaps) cities = cities.filter(c => Object.keys(c.category_coverage || {}).length < 5);
    if (filters.emailReady) cities = cities.filter(c => c.email_coverage_pct >= 30);
    if (filters.dlqItems) cities = cities.filter(c => c.dlq_items > 0);

    return cities;
  }, [data, layers, filters]);

  // Active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    Object.values(filters).forEach(v => {
      if (Array.isArray(v) && v.length > 0) count++;
      else if (v && typeof v !== "object") count++;
    });
    return count;
  }, [filters]);

  // Count per accordion group
  const groupFilterCounts = useMemo(() => {
    const counts = {};
    ACCORDION_CATS.forEach(cat => {
      let c = 0;
      cat.filters.forEach(f => {
        const v = filters[f.id];
        if (Array.isArray(v) && v.length) c++;
        else if (v && typeof v !== "object") c++;
      });
      counts[cat.id] = c;
    });
    return counts;
  }, [filters]);

  // Active legend items
  const legendItems = useMemo(() => {
    const items = [];
    if (viewMode === "confidence") {
      items.push({ color: CONF_COLORS.low, label: "< 0.4" }, { color: CONF_COLORS.mid, label: "0.4-0.6" }, { color: CONF_COLORS.high, label: "0.6-0.8" }, { color: CONF_COLORS.top, label: "> 0.8" });
    } else {
      Object.entries(STATUS_LABELS).forEach(([k, v]) => items.push({ color: STATUS_COLORS[k], label: v, hollow: k === "hollow" }));
    }
    if (layers.emailCoverage) items.push({ color: "#8b5cf6", label: "Email Coverage" });
    if (layers.googleBudget) items.push({ color: "#ef4444", label: "Google Budget" });
    if (layers.foursquareWarning) items.push({ color: "#f59e0b", label: "Foursquare Warning" });
    if (layers.foodTrucks) items.push({ color: "#06b6d4", label: "Food Trucks" });
    if (layers.categoryGaps) items.push({ color: "#a855f7", label: "Category Gaps" });
    if (layers.popHeat) items.push({ gradient: true, label: "Pop. Density Heat" });
    if (layers.offerHeat) items.push({ gradient: true, label: "Offer Density Heat" });
    return items;
  }, [viewMode, layers]);

  const applyPreset = (preset) => { setFilters(preset.filters); toast.success(`Applied: ${preset.name}`); };
  const clearFilters = () => { setFilters({}); toast.success("Filters cleared"); };

  const addToPipeline = async (city) => {
    try {
      await axios.post(`${API}/admin/pipeline/cities/add`, { city_name: city.city_name, state_code: city.state_code, population_density_rank: 999 });
      toast.success(`${city.city_name} added to pipeline`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const prioritize = async (cityId) => {
    try {
      await axios.post(`${API}/admin/pipeline/prioritize`, { city_id: cityId });
      toast.success("City prioritized");
    } catch { toast.error("Failed to prioritize"); }
  };

  const exportCSV = (type) => {
    if (!data?.cities) return;
    let csv = "", filename = "";
    const cities = data.cities.filter(c => !c.is_gap);
    if (type === "coverage") {
      csv = "City,State,Phase,Confidence,Businesses,Offers,Emails,Email%\n" +
        cities.map(c => `${c.city_name},${c.state_code},${c.current_phase},${c.confidence_score},${c.businesses_discovered},${c.birthday_offers},${c.emails_collected},${c.email_coverage_pct}`).join("\n");
      filename = "city_coverage.csv";
    } else if (type === "offers_by_state") {
      const byState = {};
      cities.forEach(c => { byState[c.state_code] = (byState[c.state_code] || 0) + c.birthday_offers; });
      csv = "State,Birthday Offers\n" + Object.entries(byState).sort((a,b) => b[1]-a[1]).map(([s,c]) => `${s},${c}`).join("\n");
      filename = "birthday_offers_by_state.csv";
    } else {
      csv = "City,State,Email Coverage %,Businesses,Emails\n" +
        cities.filter(c => c.email_coverage_pct >= 30).map(c => `${c.city_name},${c.state_code},${c.email_coverage_pct},${c.businesses_discovered},${c.emails_collected}`).join("\n");
      filename = "email_ready_cities.csv";
    }
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPNG = async (mode) => {
    try {
      const { default: html2canvas } = await import("html2canvas");
      const el = document.querySelector('[data-testid="coverage-map-container"]');
      if (!el) return;
      const canvas = await html2canvas(el, { useCORS: true, scale: 2 });
      const link = document.createElement("a");
      link.download = `coverage_map_${mode}_${new Date().toISOString().slice(0,10)}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success(`${mode} PNG exported`);
    } catch {
      toast.error("PNG export requires html2canvas — using fallback");
      // Fallback: just download CSV
      exportCSV("coverage");
    }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-amber-500" /></div>;
  if (!data) return <p className="text-sm text-slate-400 text-center py-4">Unable to load coverage map</p>;

  const mapHeight = fullscreen ? "calc(100vh - 48px)" : "480px";
  const sweepActive = data.sweep_active;

  return (
    <div className={`space-y-4 ${fullscreen ? "h-screen overflow-hidden" : ""}`} data-testid="coverage-map-panel">
      <style>{pulseStyle}</style>

      {/* Header with controls */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {sweepActive && (
            <span className="flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 animate-pulse" data-testid="sweep-live-indicator">
              <Activity className="w-3 h-3" /> LIVE
            </span>
          )}
          <button onClick={() => setViewMode(viewMode === "phase" ? "confidence" : "phase")} className="px-2 py-1 rounded-lg text-[10px] font-semibold bg-slate-100 text-slate-600 hover:bg-slate-200" data-testid="view-mode-toggle">
            {viewMode === "phase" ? "Confidence View" : "Phase View"}
          </button>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setShowFilters(!showFilters)} className="relative px-2 py-1 rounded-lg text-[10px] font-semibold bg-slate-100 text-slate-600 hover:bg-slate-200" data-testid="filter-toggle-btn">
            <Filter className="w-3 h-3 inline mr-0.5" />Filters
            {activeFilterCount > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-500 text-white text-[8px] font-bold flex items-center justify-center" data-testid="active-filter-badge">{activeFilterCount}</span>}
          </button>
          <button onClick={() => setShowLayers(!showLayers)} className="px-2 py-1 rounded-lg text-[10px] font-semibold bg-slate-100 text-slate-600 hover:bg-slate-200" data-testid="layers-toggle-btn">
            <Layers className="w-3 h-3 inline mr-0.5" />Layers
          </button>
          <button onClick={() => setShowSidebar(!showSidebar)} className="px-2 py-1 rounded-lg text-[10px] font-semibold bg-slate-100 text-slate-600 hover:bg-slate-200" data-testid="sidebar-toggle-btn">
            <BarChart3 className="w-3 h-3 inline mr-0.5" />Stats
          </button>
          <div className="relative">
            <button onClick={() => setShowExport(!showExport)} className="px-2 py-1 rounded-lg text-[10px] font-semibold bg-slate-100 text-slate-600 hover:bg-slate-200" data-testid="export-btn">
              <Download className="w-3 h-3 inline mr-0.5" />Export
            </button>
            {showExport && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-50 py-1 w-56" data-testid="export-dropdown">
                <button onClick={() => { exportPNG("map"); setShowExport(false); }} className="w-full text-left px-3 py-1.5 text-[11px] hover:bg-slate-50">Export Map PNG</button>
                <button onClick={() => { exportPNG("presentation"); setShowExport(false); }} className="w-full text-left px-3 py-1.5 text-[11px] hover:bg-slate-50">Export Presentation PNG</button>
                <button onClick={() => { exportPNG("stats_overlay"); setShowExport(false); }} className="w-full text-left px-3 py-1.5 text-[11px] hover:bg-slate-50">Export Stats Overlay PNG</button>
                <hr className="my-1 border-slate-100" />
                <button onClick={() => { exportCSV("coverage"); setShowExport(false); }} className="w-full text-left px-3 py-1.5 text-[11px] hover:bg-slate-50">City Coverage CSV</button>
                <button onClick={() => { exportCSV("offers_by_state"); setShowExport(false); }} className="w-full text-left px-3 py-1.5 text-[11px] hover:bg-slate-50">Birthday Offers by State CSV</button>
                <button onClick={() => { exportCSV("email_ready"); setShowExport(false); }} className="w-full text-left px-3 py-1.5 text-[11px] hover:bg-slate-50">Email-Ready Cities CSV</button>
              </div>
            )}
          </div>
          {!fullscreen && (
            <button onClick={() => window.open("/admin/coverage-map/fullscreen", "_blank")} className="px-2 py-1 rounded-lg text-[10px] font-semibold bg-slate-100 text-slate-600 hover:bg-slate-200" data-testid="fullscreen-btn">
              <Maximize2 className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* Layer Toggles Panel */}
      {showLayers && (
        <div className="rounded-xl border border-slate-200 p-3 bg-white" data-testid="layers-panel">
          <p className="text-[10px] font-bold text-slate-500 uppercase mb-2">Layers ({ALL_LAYERS.filter(l => layers[l.id]).length} active)</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1">
            {ALL_LAYERS.map(l => {
              const fsDate = data.foursquare_change_date || "2026-06-01";
              const fsDisabled = l.id === "foursquareWarning" && new Date() > new Date(fsDate.replace(/-/g,"/").replace("01","02"));
              return (
                <label key={l.id} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[10px] cursor-pointer ${layers[l.id] ? "bg-amber-50 border border-amber-200" : "bg-slate-50 border border-slate-100"} ${fsDisabled ? "opacity-40" : ""}`}>
                  <input type="checkbox" checked={!!layers[l.id]} disabled={fsDisabled} onChange={() => setLayers(p => ({ ...p, [l.id]: !p[l.id] }))} className="w-3 h-3 rounded text-amber-500" />
                  <span className={layers[l.id] ? "text-amber-700 font-semibold" : "text-slate-500"}>{l.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Accordion Filter Panel */}
      {showFilters && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="accordion-filter-panel">
          <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold text-slate-500 uppercase">Filters</span>
              {activeFilterCount > 0 && <span className="px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-amber-500 text-white">{activeFilterCount}</span>}
            </div>
            <div className="flex gap-1">
              <select onChange={(e) => { const p = FILTER_PRESETS.find(p => p.name === e.target.value); if (p) applyPreset(p); }} className="text-[10px] border border-slate-200 rounded px-1 py-0.5" data-testid="filter-preset-select">
                <option value="">Presets</option>
                {FILTER_PRESETS.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
              </select>
              <button onClick={clearFilters} className="text-[10px] text-red-500 font-semibold" data-testid="clear-filters-btn">Clear All</button>
            </div>
          </div>
          {ACCORDION_CATS.map(cat => (
            <div key={cat.id} className="border-b border-slate-100 last:border-0">
              <button onClick={() => setExpandedAccordion(expandedAccordion === cat.id ? null : cat.id)} className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50" data-testid={`accordion-${cat.id}`}>
                <span className="text-xs font-semibold text-slate-700">{cat.name}</span>
                <div className="flex items-center gap-1">
                  {groupFilterCounts[cat.id] > 0 && <span className="px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-amber-500 text-white" data-testid={`filter-count-${cat.id}`}>{groupFilterCounts[cat.id]}</span>}
                  <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform ${expandedAccordion === cat.id ? "rotate-180" : ""}`} />
                </div>
              </button>
              {expandedAccordion === cat.id && (
                <div className="px-3 pb-2 space-y-2">
                  {cat.filters.map(f => (
                    <div key={f.id} className="flex items-center justify-between gap-2">
                      <span className="text-[10px] text-slate-500 min-w-[80px]">{f.label}</span>
                      {f.type === "toggle" && (
                        <button onClick={() => setFilters(p => ({ ...p, [f.id]: !p[f.id] }))}
                          className={`w-8 h-4 rounded-full transition-colors ${filters[f.id] ? "bg-amber-500" : "bg-slate-300"}`} data-testid={`filter-${f.id}`}>
                          <div className={`w-3 h-3 rounded-full bg-white shadow mx-0.5 transition-transform ${filters[f.id] ? "translate-x-4" : ""}`} />
                        </button>
                      )}
                      {f.type === "range" && (
                        <input type="range" min={f.min} max={f.max} step={f.step || 1}
                          value={filters[f.id] || f.min} onChange={(e) => setFilters(p => ({ ...p, [f.id]: parseFloat(e.target.value) }))}
                          className="w-24 h-1 accent-amber-500" data-testid={`filter-${f.id}`} />
                      )}
                      {f.type === "multi" && (
                        <select multiple className="text-[9px] border border-slate-200 rounded h-8 w-28" data-testid={`filter-${f.id}`}
                          value={filters[f.id] || []} onChange={(e) => { const v = [...e.target.selectedOptions].map(o => o.value); setFilters(p => ({ ...p, [f.id]: v })); }}>
                          {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      )}
                      {f.type === "checkboxes" && (
                        <div className="flex flex-wrap gap-1">
                          {(f.options || []).map(o => (
                            <label key={o} className="flex items-center gap-0.5 text-[9px]">
                              <input type="checkbox" checked={(filters[f.id] || []).includes(o)} className="w-2.5 h-2.5"
                                onChange={() => { const cur = filters[f.id] || []; setFilters(p => ({ ...p, [f.id]: cur.includes(o) ? cur.filter(x => x !== o) : [...cur, o] })); }} />
                              {String(o)}
                            </label>
                          ))}
                        </div>
                      )}
                      {f.type === "select" && (
                        <select className="text-[9px] border border-slate-200 rounded px-1 py-0.5" value={filters[f.id] || ""} data-testid={`filter-${f.id}`}
                          onChange={(e) => setFilters(p => ({ ...p, [f.id]: e.target.value }))}>
                          <option value="">All</option>
                          {(f.options || []).map(o => <option key={o} value={o}>{o.replace(/_/g," ")}</option>)}
                        </select>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Map */}
      <div className="relative rounded-xl overflow-hidden border border-slate-200" style={{ height: mapHeight }} data-testid="coverage-map-container">
        <MapContainer center={[37.5, -119.5]} zoom={6} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <ZoomWatcher onZoom={setZoom} />
          {filteredCities.map(city => {
            const isHollow = city.status === "hollow";
            const color = viewMode === "confidence" ? confColor(city.confidence_score) : (STATUS_COLORS[city.status] || "#94a3b8");
            const radius = pinRadius(city.population || 50000);
            const pulsing = sweepActive && city.current_phase > 0 && city.current_phase <= 7 && !city.city_complete;
            return (
              <CircleMarker key={city.city_id} center={[city.lat, city.lng]} radius={radius}
                pathOptions={{ fillColor: color, color: isHollow ? "#94a3b8" : "#fff", weight: isHollow ? 2 : 1.5, fillOpacity: isHollow ? 0.1 : 0.85, dashArray: isHollow ? "4" : undefined }}>
                <Popup maxWidth={320}>
                  <div className="text-xs space-y-1.5 min-w-[200px]" data-testid={`popup-${city.city_id}`}>
                    <p className="text-sm font-black text-slate-800">{city.city_name}, {city.state_code}</p>
                    <p className="text-[10px] text-slate-400">{city.county_name}{city.county_name ? ", " : ""}{city.state_code}, United States</p>
                    {!isHollow ? (
                      <>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                          <span className="text-slate-500">Phase:</span><span className="font-semibold">{city.current_phase}</span>
                          <span className="text-slate-500">Night:</span><span className="font-semibold">{city.sweep_night}</span>
                          <span className="text-slate-500">Confidence:</span><span className="font-semibold">{city.confidence_score?.toFixed(3)}</span>
                          <span className="text-slate-500">Pop. Rank:</span><span className="font-semibold">{city.population_density_rank}</span>
                          <span className="text-slate-500">Businesses:</span><span className="font-semibold">{city.businesses_discovered}</span>
                          <span className="text-slate-500">Offers:</span><span className="font-semibold">{city.birthday_offers}</span>
                          <span className="text-slate-500">Emails:</span><span className="font-semibold">{city.emails_collected} ({city.email_coverage_pct}%)</span>
                          <span className="text-slate-500">Google:</span><span className="font-semibold">{city.google_places_used} calls</span>
                        </div>
                        {Object.keys(city.category_coverage || {}).length > 0 && (
                          <div><p className="text-[9px] font-bold text-slate-500 mt-1">Categories:</p>
                            {Object.entries(city.category_coverage).slice(0,5).map(([c,n]) => <span key={c} className="inline-block mr-1 text-[9px] bg-slate-100 rounded px-1">{c}: {n}</span>)}
                          </div>
                        )}
                        {Object.keys(city.source_coverage || {}).length > 0 && (
                          <div><p className="text-[9px] font-bold text-slate-500 mt-1">Sources:</p>
                            {Object.entries(city.source_coverage).map(([s,n]) => <span key={s} className="inline-block mr-1 text-[9px] bg-slate-100 rounded px-1">{s}: {n}</span>)}
                          </div>
                        )}
                        <div className="flex gap-1 mt-1">
                          <button onClick={() => prioritize(city.city_id)} className="px-2 py-1 rounded text-[9px] font-bold bg-amber-500 text-white">Prioritize</button>
                        </div>
                      </>
                    ) : (
                      <div>
                        <p className="text-slate-500">Pop: {city.population?.toLocaleString()}</p>
                        <button onClick={() => addToPipeline(city)} className="mt-1 px-2 py-1 rounded text-[9px] font-bold bg-emerald-600 text-white" data-testid={`add-pipeline-${city.city_id}`}>
                          <Plus className="w-2.5 h-2.5 inline mr-0.5" />Add to Pipeline
                        </button>
                      </div>
                    )}
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>

        {/* Live Legend — bottom left */}
        <div className="absolute bottom-2 left-2 z-[1000]" data-testid="live-legend">
          {showLegend ? (
            <div className="bg-white/95 backdrop-blur rounded-xl border border-slate-200 p-2 max-h-48 overflow-y-auto shadow-lg" style={{ minWidth: 140 }}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-[9px] font-bold text-slate-500 uppercase">Legend</p>
                <button onClick={() => setShowLegend(false)} className="text-slate-400"><X className="w-3 h-3" /></button>
              </div>
              {legendItems.map((item, i) => (
                <div key={i} className="flex items-center gap-1.5 py-0.5">
                  {item.gradient ? (
                    <div className="w-3 h-3 rounded-sm" style={{ background: "linear-gradient(90deg, #ef4444, #f59e0b, #22c55e)" }} />
                  ) : (
                    <div className={`w-3 h-3 rounded-full ${item.hollow ? "border-2 border-slate-400" : ""}`} style={{ backgroundColor: item.hollow ? "transparent" : item.color }} />
                  )}
                  <span className="text-[9px] text-slate-600">{item.label}</span>
                </div>
              ))}
            </div>
          ) : (
            <button onClick={() => setShowLegend(true)} className="bg-white/95 backdrop-blur rounded-full border border-slate-200 px-2 py-1 text-[9px] font-semibold text-slate-600 shadow-lg" data-testid="legend-pill">
              <Eye className="w-3 h-3 inline mr-0.5" />{legendItems.length} layers
            </button>
          )}
        </div>

        {/* Sweep velocity sidebar — right side */}
        {showSidebar && (
          <div className="absolute top-0 right-0 z-[1000] bg-white/95 backdrop-blur border-l border-slate-200 h-full w-52 overflow-y-auto p-3 space-y-3" data-testid="sweep-velocity-sidebar">
            <p className="text-[10px] font-bold text-slate-500 uppercase">Pipeline</p>
            <div>
              <p className="text-[9px] font-bold text-slate-400 mb-1">Phase 4 Slots</p>
              {(data.phase4_slots || []).length === 0 && <p className="text-[9px] text-slate-400">No active slots</p>}
              {(data.phase4_slots || []).map(s => (
                <div key={s.city_id} className="text-[10px] text-slate-600 flex items-center gap-1 py-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
                  {s.city_name}
                </div>
              ))}
            </div>
            <div>
              <p className="text-[9px] font-bold text-slate-400 mb-1">Queue (Next 5)</p>
              {(data.phase4_queue || []).slice(0,5).map((q, i) => (
                <div key={i} className="text-[10px] text-slate-500 py-0.5">{i+1}. {q.city_name || q.city_id}</div>
              ))}
            </div>
            <div>
              <p className="text-[9px] font-bold text-slate-400 mb-1">Source Health</p>
              {(data.source_health || []).slice(0,8).map(s => (
                <div key={s.source_name} className="flex items-center gap-1 text-[9px] py-0.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${(s.consecutive_zero_nights || 0) >= 2 ? "bg-red-500" : (s.consecutive_zero_nights || 0) >= 1 ? "bg-amber-400" : "bg-emerald-500"}`} />
                  <span className="text-slate-600">{s.source_name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Timeline Scrubber — desktop only */}
      <div className="hidden sm:flex items-center gap-2 px-1" data-testid="timeline-scrubber">
        <button onClick={() => setPlaying(!playing)} className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center" data-testid="scrubber-play-btn">
          {playing ? <Pause className="w-3 h-3 text-slate-600" /> : <Play className="w-3 h-3 text-slate-600" />}
        </button>
        <input type="range" min={1} max={30} value={scrubberDay} onChange={(e) => setScrubberDay(parseInt(e.target.value))} className="flex-1 h-1 accent-amber-500" data-testid="scrubber-slider" />
        <span className="text-[10px] text-slate-400 min-w-[60px]">Day {scrubberDay}/30</span>
      </div>

      {/* Metrics Timeline Chart */}
      <div className="rounded-xl border border-slate-200 p-3 bg-white" data-testid="metrics-chart-panel">
        <div className="flex items-center justify-between mb-2">
          <div className="flex gap-1">
            {TIME_RANGES.map(t => (
              <button key={t.key} onClick={() => setTimeRange(t.key)} className={`px-2 py-1 rounded text-[10px] font-semibold ${timeRange === t.key ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-500"}`} data-testid={`time-range-${t.label}`}>{t.label}</button>
            ))}
          </div>
          <div className="flex gap-1 flex-wrap">
            {Object.entries(METRIC_COLORS).map(([k, c]) => (
              <button key={k} onClick={() => setActiveMetrics(p => ({ ...p, [k]: !p[k] }))}
                className={`px-1.5 py-0.5 rounded text-[9px] font-semibold border ${activeMetrics[k] ? "border-slate-300 bg-white" : "border-transparent bg-slate-50 opacity-40"}`}
                data-testid={`metric-toggle-${k}`}>
                <span className="inline-block w-2 h-2 rounded-full mr-0.5" style={{ backgroundColor: c }} />
                {k.replace(/_/g, " ")}
              </button>
            ))}
          </div>
        </div>
        <div style={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={metricsData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <RTooltip contentStyle={{ fontSize: 11 }} />
              <RLegend wrapperStyle={{ fontSize: 10 }} />
              {activeMetrics.businesses && <Line type="monotone" dataKey="businesses" stroke={METRIC_COLORS.businesses} strokeWidth={2} dot={false} />}
              {activeMetrics.offers && <Line type="monotone" dataKey="offers" stroke={METRIC_COLORS.offers} strokeWidth={2} dot={false} />}
              {activeMetrics.emails && <Line type="monotone" dataKey="emails" stroke={METRIC_COLORS.emails} strokeWidth={2} dot={false} />}
              {activeMetrics.google_spend && <Line type="monotone" dataKey="google_spend" stroke={METRIC_COLORS.google_spend} strokeWidth={2} dot={false} />}
              {activeMetrics.cities_completed && <Line type="monotone" dataKey="cities_completed" stroke={METRIC_COLORS.cities_completed} strokeWidth={2} dot={false} />}
            </LineChart>
          </ResponsiveContainer>
        </div>
        {metricsData.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No metrics data for selected range</p>}
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
        {Object.entries(STATUS_LABELS).map(([key, label]) => {
          const count = filteredCities.filter(c => c.status === key).length;
          return (
            <div key={key} className="rounded-lg border border-slate-200 p-2" data-testid={`map-stat-${key}`}>
              <p className="text-lg font-bold" style={{ color: STATUS_COLORS[key] || "#94a3b8" }}>{count}</p>
              <p className="text-[9px] text-slate-500 uppercase">{label}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
